/**
 * @file Dashboard (Inicio) — restyled to match design page-inicio.jsx exactly.
 *
 * Layout:
 *  - cd-hero: greeting + date eyebrow + share-card aside
 *  - cd-grid (12 cols):
 *    - cd-timeline-card (span 7): today's appointments timeline
 *    - cd-right-col (span 5): cd-next-card (gradient) + cd-plan-card
 *    - cd-stats-strip (span 12): 4-cell stats
 *    - cd-perf-row: cd-chart-card (span 7) + cd-patients-card (span 5)
 *
 * Uses design CSS classes from src/client/styles/cd.css (cd-* prefix).
 */
import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useDashboard } from "../hooks/useDashboard";
import { useProfessionalProfile } from "../hooks/useProfessionals";
import { appointmentService } from "../services/appointment";
import { patientService } from "../services/patient";
import { toast } from "../ui";

const SVG_PROPS = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" };

const I = {
  Calendar: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.6">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  ),
  Users: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.6">
      <path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
      <circle cx="9" cy="7" r="3.5" />
      <path d="M22 19v-1a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Check: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="2">
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
  X: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="2">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  Trend: ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.7">
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M14 7h7v7" />
    </svg>
  ),
  Copy: ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.7">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  ),
  Share: ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.7">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <path d="M16 6l-4-4-4 4M12 2v13" />
    </svg>
  ),
  Arrow: ({ size = 12 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="2">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  File: ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.7">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </svg>
  ),
  Phone: ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.7">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />
    </svg>
  ),
  Bars: ({ size = 36 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" {...SVG_PROPS} strokeWidth="1.4">
      <path d="M3 21h18" />
      <rect x="5" y="12" width="3" height="7" rx="1" />
      <rect x="10.5" y="7" width="3" height="12" rx="1" />
      <rect x="16" y="14" width="3" height="5" rx="1" />
    </svg>
  ),
};

const greetingFor = (h) => (h < 12 ? "Buenos días" : h < 18 ? "Buenas tardes" : "Buenas noches");

const formatDateEyebrow = (d) => {
  // Match design eyebrow format: "Sábado · 25 Abril 2026" (middle-dot,
  // capitalized month, no "de", no commas).
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const weekday = cap(d.toLocaleDateString("es-MX", { weekday: "long" }));
  const day = d.getDate();
  const month = cap(d.toLocaleDateString("es-MX", { month: "long" }));
  const year = d.getFullYear();
  return `${weekday} · ${day} ${month} ${year}`;
};

const formatTimeShort = (iso) =>
  new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });

const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

const initials = (name) =>
  (name || "").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?";

const PLAN_LABELS = { PRO_PLUS: "Pro", PRO_TRIAL: "Pro Trial", PRO: "Pro", GRATIS: "Gratis" };

const AVATAR_PALETTE = ["a1", "a2", "a3", "a4", "a5", "a6"];

function CD_Hero({ greeting, displayName, dateLabel, todayCount, completedToday, inProgressToday, profileUrl, onCopy, copied }) {
  return (
    <div className="cd-hero">
      <div>
        <div className="cd-eyebrow">{dateLabel}</div>
        <h1>
          {greeting}, <em>{displayName}</em>
        </h1>
        <div className="cd-sub">
          {todayCount > 0 ? (
            <>
              Tienes <strong style={{ color: "var(--ink)" }}>{todayCount} {todayCount === 1 ? "cita" : "citas"}</strong>{" "}
              programadas para hoy
              {completedToday > 0 ? ` · ${completedToday} ya completadas` : ""}
              {inProgressToday > 0 ? `, ${inProgressToday} en curso` : ""}
              .
            </>
          ) : (
            "Comparte tu perfil para empezar a recibir citas."
          )}
        </div>
      </div>
      <div className="cd-hero-aside">
        <div className="cd-share-card">
          <div>
            <div className="cd-label">Tu perfil público</div>
            <div className="cd-url">{profileUrl ? profileUrl.replace(/^https?:\/\//, "") : "Configura tu slug en Mi perfil"}</div>
          </div>
          <button type="button" onClick={onCopy} disabled={!profileUrl}>
            <I.Copy /> {copied ? "Copiado" : "Copiar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CD_Timeline({ appointments, isLoading }) {
  const isEmpty = !isLoading && appointments.length === 0;
  const navigate = useNavigate();
  const minTime = appointments[0] ? formatTimeShort(appointments[0].dateTime) : null;
  const maxTime = appointments[appointments.length - 1]
    ? formatTimeShort(appointments[appointments.length - 1].dateTime)
    : null;
  const liveAppt = appointments.find((a) => a.status === "ASISTENCIA_CONFIRMADA");
  const liveLabel = liveAppt
    ? new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false })
    : null;

  return (
    <section className="cd-card cd-timeline-card">
      <div className="cd-timeline-head">
        <div>
          <h2 className="cd-section-title">
            Agenda de <em>hoy</em>
          </h2>
          <div className="cd-section-meta" style={{ marginTop: 4 }}>
            {appointments.length > 0
              ? `${appointments.length} ${appointments.length === 1 ? "cita programada" : "citas programadas"}${
                  minTime && maxTime ? ` · ${minTime} – ${maxTime}` : ""
                }`
              : "Sin citas programadas"}
          </div>
        </div>
        <div className="cd-meta">
          {liveLabel && <span className="cd-now-pill">En vivo · {liveLabel}</span>}
          <RouterLink to="/calendario" className="cd-link-action">
            Ver semana <I.Arrow />
          </RouterLink>
        </div>
      </div>
      {isLoading ? (
        <div className="cd-timeline" style={{ minHeight: 240, display: "grid", placeItems: "center", color: "var(--ink-3)" }}>
          Cargando…
        </div>
      ) : isEmpty ? (
        <div className="cd-empty" style={{ minHeight: 360 }}>
          <div className="glyph"><I.Calendar /></div>
          <h4>No tienes citas programadas hoy</h4>
          <p>Comparte tu enlace público para empezar a recibir reservas.</p>
          <button className="cd-btn-primary" onClick={() => navigate("/mi-perfil")}>
            <I.Share /> Compartir perfil
          </button>
        </div>
      ) : (
        <div className="cd-timeline">
          {appointments.map((a, i) => {
            const startTime = formatTimeShort(a.dateTime);
            const duration = Math.round(
              (new Date(a.endTime).getTime() - new Date(a.dateTime).getTime()) / 60000
            );
            const statusKey =
              a.status === "COMPLETADA"
                ? "completada"
                : a.status === "CONFIRMADA" || a.status === "ASISTENCIA_CONFIRMADA"
                  ? "proxima"
                  : a.status === "AGENDADA"
                    ? "proxima"
                    : "proxima";
            const statusLabel =
              a.status === "COMPLETADA"
                ? "Completada"
                : a.status === "CONFIRMADA" || a.status === "ASISTENCIA_CONFIRMADA"
                  ? "Confirmada"
                  : "Próxima";
            return (
              <div
                key={a.id}
                className="cd-tl-row"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/cita/${a.id}`)}
              >
                <div className="cd-tl-time">
                  <strong>{startTime}</strong>
                  {duration ? `${duration} min` : null}
                </div>
                <div className="cd-tl-body">
                  <div className={"cd-avatar " + AVATAR_PALETTE[i % AVATAR_PALETTE.length]}>
                    {initials(a.patient?.user?.firstName + " " + (a.patient?.user?.lastName || ""))}
                  </div>
                  <div className="cd-tl-info">
                    <div className="cd-name">
                      {(a.patient?.user?.firstName || "") + " " + (a.patient?.user?.lastName || "")}
                      {a.patient?.dateOfBirth && (
                        <span className="cd-age"> · {ageFromDob(a.patient.dateOfBirth)}a</span>
                      )}
                    </div>
                    <div className="cd-type">
                      {a.service?.name || a.motivo || "Consulta"}
                    </div>
                  </div>
                </div>
                <div className={"cd-tl-status " + statusKey}>{statusLabel}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CD_NextCard({ next }) {
  const navigate = useNavigate();
  if (!next) {
    return (
      <div className="cd-card" style={{ padding: 22 }}>
        <div className="cd-eyebrow" style={{ color: "var(--accent-deep)" }}>Próxima cita</div>
        <div className="cd-empty" style={{ padding: "8px 0 0" }}>
          <h4>Sin próximas citas</h4>
          <p style={{ marginBottom: 0 }}>Tu agenda se mostrará aquí en cuanto recibas reservas.</p>
        </div>
      </div>
    );
  }
  const start = new Date(next.dateTime);
  const dateLabel = start.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
  const timeStart = formatTimeShort(next.dateTime);
  return (
    <div className="cd-next-card cd-card">
      <div className="cd-next-inner">
        <div className="cd-eyebrow">Próxima cita</div>
        <h3 className="cd-next-name">{next.patientName || "Paciente"}</h3>
        <div className="cd-next-meta">
          {dateLabel} · {timeStart}
          {next.serviceName ? ` · ${next.serviceName}` : ""}
        </div>
        <div className="cd-next-actions">
          <button
            type="button"
            className="solid"
            onClick={() => navigate(`/cita/${next.id}`)}
          >
            <I.File /> Ver detalle
          </button>
          {next.status === "AGENDADA" && (
            <button type="button" onClick={() => navigate(`/cita/${next.id}`)}>
              <I.Phone /> Confirmar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CD_PlanCard({ subscription }) {
  const navigate = useNavigate();
  const plan = subscription?.plan || "GRATIS";
  const used = subscription?.appointments?.current || 0;
  const limit = subscription?.appointments?.limit;
  const isUnlimited = !limit || limit === Infinity;
  const pct = isUnlimited ? 0 : limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const planLabel = PLAN_LABELS[plan] || plan;
  return (
    <div className="cd-card cd-plan-card">
      <div className="cd-plan-head">
        <div>
          <div className="cd-section-meta" style={{ marginBottom: 4 }}>Tu plan</div>
          <div className="cd-plan-numbers">
            <span className="big">{used}</span>
            <span className="of">{isUnlimited ? "citas" : `/ ${limit} citas`}</span>
          </div>
          <div className="cd-plan-sub">Este mes · plan {planLabel}</div>
        </div>
        <span className="cd-plan-tag">{planLabel}</span>
      </div>
      {!isUnlimited && (
        <div className="cd-plan-bar">
          <div className="cd-plan-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      )}
      {plan === "GRATIS" && (
        <div className="cd-plan-cta">
          <div className="cd-copy">
            <strong>Sube a Pro</strong> — más citas y recordatorios por WhatsApp.
          </div>
          <button type="button" onClick={() => navigate("/mi-plan")}>
            Sube a Pro
          </button>
        </div>
      )}
    </div>
  );
}

function CD_StatsStrip({ stats }) {
  const cells = [
    {
      lbl: "Total pacientes",
      num: stats?.totalPatients ?? 0,
      icon: <I.Users size={16} />,
      delta: "Acumulado",
    },
    {
      lbl: "Completadas (mes)",
      num: stats?.monthlyCompleted ?? 0,
      icon: <I.Check size={14} />,
      delta: "Este mes",
    },
    {
      lbl: "Tasa de confirmación",
      num: stats?.confirmationRate ?? 0,
      suffix: "%",
      icon: <I.Trend size={14} />,
      delta: "Este mes",
    },
    {
      lbl: "Cancelaciones",
      num: (stats?.monthlyCancelled || 0) + (stats?.monthlyNoShow || 0),
      icon: <I.X size={14} />,
      delta: "Este mes",
    },
  ];
  return (
    <div className="cd-stats-strip">
      {cells.map((c) => (
        <div className="cd-stat-cell" key={c.lbl}>
          <div className="top">
            <span className="lbl">{c.lbl}</span>
            <span className="icon">{c.icon}</span>
          </div>
          <div className="num">
            {c.num}
            {c.suffix && <span className="pct">{c.suffix}</span>}
          </div>
          <div className="delta">{c.delta}</div>
        </div>
      ))}
    </div>
  );
}

function CD_PerformancePlaceholder() {
  return (
    <section className="cd-card cd-chart-card cd-chart-placeholder">
      <div className="cd-section-head">
        <div>
          <h2 className="cd-section-title">
            Rendimiento <em>de la semana</em>
          </h2>
          <div className="cd-section-meta" style={{ marginTop: 4 }}>
            Confirmaciones, asistencia y duración promedio
          </div>
        </div>
        <span className="cd-soon-pill">Próximamente</span>
      </div>
      <div className="cd-chart-empty">
        <div className="glyph"><I.Bars /></div>
        <p>Estamos preparando tu reporte semanal.</p>
      </div>
    </section>
  );
}

function CD_Patients({ patients, isLoading }) {
  const navigate = useNavigate();
  const isEmpty = !isLoading && patients.length === 0;
  return (
    <section className="cd-card cd-patients-card">
      <div className="head">
        <div>
          <h2 className="cd-section-title">
            Pacientes <em>recientes</em>
          </h2>
          <div className="cd-section-meta" style={{ marginTop: 4 }}>
            Últimos atendidos
          </div>
        </div>
        <RouterLink to="/pacientes" className="cd-link-action">
          Todos <I.Arrow />
        </RouterLink>
      </div>
      {isLoading ? (
        <div className="cd-empty" style={{ minHeight: 200 }}>Cargando…</div>
      ) : isEmpty ? (
        <div className="cd-empty" style={{ minHeight: 280 }}>
          <div className="glyph"><I.Users /></div>
          <h4>Aún no hay pacientes</h4>
          <p>Tus pacientes aparecerán aquí después de su primera cita.</p>
        </div>
      ) : (
        <div className="cd-patients-list">
          {patients.slice(0, 6).map((p, i) => {
            const fullName = `${p.user?.firstName || ""} ${p.user?.lastName || ""}`.trim();
            return (
              <div
                key={p.id}
                className="cd-p-row"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/paciente/${p.id}`)}
              >
                <div className={"cd-avatar " + AVATAR_PALETTE[i % AVATAR_PALETTE.length]}>
                  {initials(fullName)}
                </div>
                <div className="cd-p-info">
                  <div className="n">{fullName || "Paciente"}</div>
                  <div className="c">{p.user?.email || p.user?.phone || ""}</div>
                </div>
                <div className="cd-p-meta">
                  <div className="last">
                    {p.lastAppointmentAt
                      ? new Date(p.lastAppointmentAt).toLocaleDateString("es-MX", { day: "numeric", month: "short" })
                      : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.role === "PROFESIONAL";
  const { stats, subscription, isLoading: dashLoading } = useDashboard(isPro);
  const { profile } = useProfessionalProfile(isPro);
  const [todayList, setTodayList] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);
  const [recentPatients, setRecentPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (user?.role === "PACIENTE") navigate("/mi-salud", { replace: true });
    if (user?.role === "ADMIN") navigate("/cx", { replace: true });
  }, [user?.role, navigate]);

  React.useEffect(() => {
    if (isPro && user?.onboardingComplete === false) {
      navigate("/onboarding", { replace: true });
    }
  }, [isPro, user?.onboardingComplete, navigate]);

  // Load today's appointments
  useEffect(() => {
    if (!isPro) return;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    setTodayLoading(true);
    appointmentService
      .getAll({ dateFrom: start.toISOString(), dateTo: end.toISOString() })
      .then((r) => setTodayList((r.data || []).filter((a) => a.status !== "CANCELADA")))
      .catch(() => setTodayList([]))
      .finally(() => setTodayLoading(false));
  }, [isPro]);

  // Load recent patients
  useEffect(() => {
    if (!isPro) return;
    setPatientsLoading(true);
    patientService
      .getAll()
      .then((r) => setRecentPatients(r.data || []))
      .catch(() => setRecentPatients([]))
      .finally(() => setPatientsLoading(false));
  }, [isPro]);

  const profileUrl = profile?.slug ? `https://consultto.mx/c/${profile.slug}` : null;

  const handleCopy = async () => {
    if (!profileUrl) return;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const greeting = greetingFor(new Date().getHours());
  const displayName = [profile?.title, user?.firstName].filter(Boolean).join(" ").trim() || user?.firstName || "";
  const dateLabel = formatDateEyebrow(new Date());

  const todayCount = todayList.length;
  const completedToday = todayList.filter((a) => a.status === "COMPLETADA").length;
  const inProgressToday = todayList.filter((a) => a.status === "ASISTENCIA_CONFIRMADA").length;

  return (
    <>
      <CD_Hero
        greeting={greeting}
        displayName={displayName}
        dateLabel={dateLabel}
        todayCount={todayCount}
        completedToday={completedToday}
        inProgressToday={inProgressToday}
        profileUrl={profileUrl}
        onCopy={handleCopy}
        copied={copied}
      />
      <div className="cd-grid">
        <CD_Timeline appointments={todayList} isLoading={todayLoading} />
        <div className="cd-right-col">
          <CD_NextCard next={stats?.nextAppointment} />
          <CD_PlanCard subscription={subscription} />
        </div>
        <CD_StatsStrip stats={stats} />
        <div className="cd-perf-row">
          <CD_PerformancePlaceholder />
          <CD_Patients patients={recentPatients} isLoading={patientsLoading} />
        </div>
      </div>
    </>
  );
}
